import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as styles from './bulk-download-talent-profile.module.scss';
import { BasicButton, BasicLoading, BasicTable } from '../../../../assets/common';
import { remove_card } from '../../../../assets/img';
import { adminApi } from '../../../../services/admin';
import { debounce, uniq, get } from 'lodash';
import { Spin } from 'antd';
import TalentProfileDownloadPreview from '../../../../components/TalentProfileDownloadPreview/TalentProfileDownloadPreview';
import talentProfileApi, {
  fetchTalentProfilePersonality,
  fetchTalentProfileSummary,
} from '../../../../services/talentProfiles';
import { PERSONALITY_SORT_ORDER } from '../../../../utils/constants';
import { sortCollectionByField } from '../../../../utils/helper';
import { setTokenTalentProfile } from '../../../../store/pageSlice';
import { getFirstProfiles, getSecondProfiles, getThirdProfiles } from '../../../TalentProfilePage/store/profileSlice';
import BasicTreeSelect from '../../../../components/BasicTreeSelectForDownload/BasicTreeSelect';
import { updateDownloadSettingRadio, updateDownloadSettingSelection, clearDownloadData } from '../../../TalentProfilePage/store/downloadProfileSlice';

import { TP_PRINT_DEFAULT_RADIO_OPTIONS, TP_PRINT_DEFAULT_SELECT_OPTIONS, TP_PRINT_SELECT_OPTIONS, TP_PRINT_RADIO_MAPPING_PREFIX_VALUE } from '../../../../components/TalentProfileDownloadPreview/constants';
import { pushMessage } from '../../../../store/alertMessageSlice';
import { AN_UNEXPECTED_ERROR, MESSAGE_TYPES } from '../../../../utils/constants';
import moment from 'moment';
import download_file_icon from '../../../../assets/img/download_file.svg';
import pause_icon from '../../../../assets/img/pause.svg';
import play_icon from '../../../../assets/img/play.svg';
import retry_icon from '../../../../assets/img/retry.svg';
import close_icon from '../../../../assets/img/close_2.svg';

const MAX_STAFF_IDS = 100;
const LIMIT_LOADING = 20;

const BulkDownloadTalentProfile = () => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [notExistStaffIDs, setNotExistStaffIDs] = useState([]);
  const [isViewingSample, setIsViewingSample] = useState(false);
  const [qualification, setQualification] = useState([]);
  const [langAndBoard, setLangAndBoard] = useState([]);
  const [lang, setLang] = useState([]);
  const [areaOfExpertise, setAreaOfExpertise] = useState('');
  const [carreerAspiration, setCarreerAspiration] = useState('');
  const [personality, setPersonality] = useState([]);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { selectedOptions: prevSelectedOption, radioOptions: prevRadioOptions } = useSelector(
    (state) => state.app.talentProfile.downloadProfile.downloadSettings
  );
  const [selectedOptions, setSelectedOptions] = useState(TP_PRINT_DEFAULT_SELECT_OPTIONS);
  const [radioOptions, setRadioOptions] = useState(TP_PRINT_DEFAULT_RADIO_OPTIONS);

  const handleChangeRadio = (name, parentName) => {
    setRadioOptions((prev) => ({
      ...prev,
      [parentName]: name,
    }));
  };

  const [downloadActivityLogsTable, setDownloadActivityLogsTable] = useState({ page: 1, limit: 5, data: [], total: 0 });

  const handleFilterApply = async () => {
    dispatch(updateDownloadSettingSelection(selectedOptions));
    dispatch(updateDownloadSettingRadio(radioOptions));
  };

  const [showSuggestions, setShowSuggestions] = useState({
    isShow: false,
    top: 0,
    left: 0,
  });
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const [loadingPersonality, setLoadingPersonality] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const tokenTalentProfile = useSelector((state) => state.page.tokenTalentProfile);

  const handleContainerClick = () => {
    inputRef.current.focus();
  };

  const loadMoreSuggestions = useCallback(
    async (page, staff_id, currentSuggestions = []) => {
      setIsLoading(true);
      try {
        const res = await adminApi.getSuggestionBulkDownloadMatchingStaffID({ page, staff_id, limit: LIMIT_LOADING });
        const newItems = res.data.result.data;
        const total = res.data.result.total_results;
        const newSuggestions = [...currentSuggestions, ...newItems].filter((item) => !selectedItems.includes(item));
        setSuggestions(newSuggestions);
        setHasMore(total / LIMIT_LOADING > page);
        setCurrentPage(page);
        setShowSuggestions((prev) => ({ ...prev, isShow: staff_id && Boolean(newSuggestions.length) }));
      } catch (error) {
        console.error('Error loading more suggestions:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedItems]
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    if (inputRef.current) {
      const input = inputRef.current;
      input.style.width = `${input?.value?.length || 2}ch`;
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        if (value.trim() === '') {
          setSuggestions([]);
          setShowSuggestions((prev) => ({ ...prev, isShow: false }));
          return;
        }
        loadMoreSuggestions(1, value);
      }, 500),
    [loadMoreSuggestions]
  );

  useEffect(() => {
    debouncedSearch(inputValue);
    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, debouncedSearch]);

  const handleScroll = useCallback(
    debounce(() => {
      const dropdown = dropdownRef.current;

      if ((!dropdown && !showSuggestions.isShow) || !hasMore || isLoading) return;

      const { scrollTop, scrollHeight, clientHeight } = dropdown;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        loadMoreSuggestions(currentPage + 1, inputValue, suggestions);
      }
    }, 200),
    [currentPage, showSuggestions.isShow, hasMore, isLoading, loadMoreSuggestions]
  );

  useEffect(() => {
    const dropdown = dropdownRef.current;
    if (!dropdown) return;

    dropdown.addEventListener('scroll', handleScroll);
    return () => dropdown.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleSelectItem = (item) => {
    if (selectedItems.length < MAX_STAFF_IDS) {
      setShowSuggestions((prev) => ({ ...prev, isShow: false }));
      setSelectedItems([...selectedItems, item]);
      setInputValue('');
      setActiveSuggestionIndex(0);
      setTimeout(() => {
        handleContainerClick();
      }, 100);
      handleValidate(selectedItems, [item]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions((prev) => ({ ...prev, isShow: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetSuggestionListStaffID = () => {
    const input = inputRef.current;
    const container = containerRef.current;
    if (!input || !container) return;
    const inputRect = input.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const top = `${inputRect.top - containerRect.top + input.offsetHeight}px`;
    const left = `${inputRect.left - containerRect.left}px`;
    return { top, left };
  };

  const handleInputFocus = (e) => {
    const { top, left } = resetSuggestionListStaffID();
    setShowSuggestions({ isShow: Boolean(e.target.value), top, left });
  };

  const handleRemoveItem = (staffId) => {
    setSelectedItems((prev) => prev.filter((p) => p !== staffId));
    setNotExistStaffIDs((prev) => prev.filter((p) => p !== staffId));

    setTimeout(() => {
      const { top, left } = resetSuggestionListStaffID();
      setShowSuggestions({ isShow: false, top, left });
    }, 100);
  };

  const handleValidate = async (selectedItems, validateStaffIDs) => {
    setValidating(true);
    try {
      const res = await adminApi.validateBulkDownloadMatchingStaffID(validateStaffIDs, user.roleActive.roleId);
      const result = res.data.result.result;
      if (result.is_valid) return;
      setNotExistStaffIDs((prev) => uniq([...prev, ...result.staff_ids]));
    } catch (error) {
      console.error('Error when validate:', error);
    } finally {
      setValidating(false);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    if (!pastedText.trim()) return;
    const items = uniq(pastedText.trim().split(/\s+/) || []).filter((p) => !selectedItems.includes(p));
    const availableSlots = MAX_STAFF_IDS - selectedItems.length;
    const itemsToAdd = items.slice(0, availableSlots);
    if (!itemsToAdd.length) return;
    const newSelected = [...selectedItems, ...itemsToAdd];
    setSelectedItems(newSelected);
    setInputValue('');
    handleValidate(selectedItems, itemsToAdd);
  };

  const handleKeyDown = (e) => {
    if (showSuggestions.isShow && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        const activeItem = document.getElementById(`suggestion-${activeSuggestionIndex + 1}`);
        activeItem?.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
        const activeItem = document.getElementById(`suggestion-${activeSuggestionIndex - 1}`);
        activeItem?.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && suggestions[activeSuggestionIndex]) {
        e.preventDefault();
        handleSelectItem(suggestions[activeSuggestionIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions((prev) => ({ ...prev, isShow: false }));
      }
    } else if (!showSuggestions.isShow && e.key === 'Enter') {
      const value = e.target.value;
      if (selectedItems.includes(value)) return;
      handleSelectItem(value);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    handleFilterApply();
    await fetchDownload();
    await fetchDownloadActivityLogsTable();
    setDownloading(false);
  };

  const fetchDownload = async () => {
    try {
      setDownloading(true);
      const selectedValues = selectedOptions.map((i) => i.value);
      const allValues = getLeafValues(TP_PRINT_SELECT_OPTIONS);
      let filter = allValues.filter((v) => !selectedValues.includes(v));
      const selectedRadioOptions = [];
      Object.keys(radioOptions).forEach((key) => {
        if (radioOptions[key]) {
          selectedRadioOptions.push(`${TP_PRINT_RADIO_MAPPING_PREFIX_VALUE[key]}_${radioOptions[key]}`);
        }
      })
      filter = filter.concat(selectedRadioOptions)

      await adminApi.downloadTalentProfiles(
        selectedItems,
        parseInt(user.data.userId),
        user.data.userName,
        user.data.email,
        user.roleActive.roleId,
        filter
      );

    } catch (error) {
      console.error('Error loading more suggestions:', error);
    } finally {
      setIsLoading(false);
      setDownloading(false);
    }
  }

  const traverse = (result, nodes) => {
    nodes.forEach((node) => {
      if (node.children?.length) {
        if (node.isRadio) return;

        traverse(result, node.children);
        return;
      }

      result.push(node.value);

    });
  };
  const getLeafValues = (options) => {
    const result = [];
    traverse(result, options);
    return result;
  };

  const handleViewSample = async () => {
    try {
      dispatch(clearDownloadData());

      handleFilterApply();

      const rs = await talentProfileApi.getTokenTalentProfile(selectedItems[0], user.roleActive.roleId);
      const token = get(rs, 'data.result');
      dispatch(setTokenTalentProfile(token));

      dispatch(getThirdProfiles({ id: selectedItems[0], roleActive: user.roleActive, permissions: user.permissions }));
      dispatch(getFirstProfiles({ id: selectedItems[0], roleActive: user.roleActive, permissions: user.permissions }));
      dispatch(getSecondProfiles({ id: selectedItems[0], roleActive: user.roleActive, permissions: user.permissions }));

      if (selectedItems.length == 0 || !user.roleActive.roleId || !token) return;

      await getTalentProfileSummary();
      await getTalentProfilePersonality(token);
      // Force remount preview when switching staff by changing a key
      setIsViewingSample(false);
      setTimeout(() => setIsViewingSample(true), 0);

    } catch (error) {
      console.log(error);
    }
  }

  const closeModal = () => {
    setIsViewingSample(false);
  };

  const getTalentProfilePersonality = async (token) => {
    try {
      setLoadingPersonality(true);
      const response = await fetchTalentProfilePersonality({ roleUser: { id: selectedItems[0], roleActive: user.roleActive, permissions: user.permissions }, id: selectedItems[0], tokenTalentProfile: token ? token : tokenTalentProfile });
      let result = response?.data?.result;
      if (result && response.data.code === 200) {
        if (Object.keys(result).length > 0) {
          const rankField = 'personality_rank';
          const sortedPersonality = sortCollectionByField(result, rankField, PERSONALITY_SORT_ORDER);
          setPersonality(sortedPersonality);
        }
      }
    } catch (error) {
      console.error(error);
      setPersonality([]);
    } finally {
      setLoadingPersonality(false);
    }
  };

  const getTalentProfileSummary = async () => {
    try {
      setLoadingSummary(true);
      const response = await fetchTalentProfileSummary({ id: selectedItems[0], roleUser: { id: selectedItems[0], roleActive: user.roleActive, permissions: user.permissions } });
      let result = response?.data?.result;

      if (result && response.data.code === 200) {
        if (result?.resBoardMembership && Object.keys(result.resBoardMembership).length > 0) {
          setLangAndBoard(result.resBoardMembership);
        } else {
          setLangAndBoard([]);
        }
        if (result?.resLanguage && Object.keys(result.resLanguage).length > 0) {
          setLang(result.resLanguage);
        } else {
          setLang([]);
        }
        if (result?.resQualification && Object.keys(result.resQualification).length > 0) {
          setQualification(result.resQualification);
        } else {
          setQualification([]);
        }
        if (result?.areaOfExpertise && result.areaOfExpertise.length > 0) {
          setAreaOfExpertise(result.areaOfExpertise[0].area_of_expertise);
        } else {
          setAreaOfExpertise('');
        }
        if (result?.careerAspirationAll && Object.keys(result.careerAspirationAll).length > 0) {
          setCarreerAspiration(result.careerAspirationAll);
        } else {
          setCarreerAspiration('');
        }
      }
    } catch (error) {
      setLangAndBoard([]);
      setLang([]);
      setQualification([]);
      setCarreerAspiration('');
      console.error(error);
    } finally {
      setLoadingSummary(false);
    }
  };

  async function fetchDownloadActivityLogsTable() {
    try {
      setLoading(true);
      const res = await adminApi.getBulkDownloadTalentProfilesDownloadActivityLogs(
        {
          page: downloadActivityLogsTable.page,
          limit: downloadActivityLogsTable.limit,
        },
        {
          user_id: parseInt(user.data.userId),
          user_email: user.data.email,
        }
      );
      const data = get(res, 'data.result.records') || [];
      const total = get(res, 'data.result.total') || 0;
      setDownloadActivityLogsTable((prev) => ({ ...prev, data, total }));
    } catch (error) {
      console.error(error);
      setDownloadActivityLogsTable({ page: 1, limit: 15, data: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      fetchDownloadActivityLogsTable();
    }, 5000);

    return () => clearInterval(interval);
  }, [downloadActivityLogsTable.page]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.title}>Bulk Download Talent Profile</div>
        <div className={styles.instruction}>
          <span>Instructions:</span> Type in the Staff ID which you would like to download separated by a new line (ENTER)
        </div>
        <div className={styles.mainContent}>
          <div className={styles.flexContent}>
            <div className={styles.label}>Insert Staff ID</div>

            <Spin spinning={validating}>
              <div className={styles.multiSelectContainer} ref={containerRef}>
                <div className={styles.inputContainer} onClick={handleContainerClick}>
                  <div className={styles.pillsContainer}>
                    {selectedItems.map((item) => {
                      const isNotValid = notExistStaffIDs.includes(item);
                      return (
                        <div key={item}>
                          <div className={styles.pill} style={{ borderColor: isNotValid && '#da2228' }}>
                            <BasicButton mode="icon" onClick={() => handleRemoveItem(item)} height={7.5}>
                              <img src={remove_card} alt="close-modal" className={styles.removeBtn} />
                            </BasicButton>
                            <span>{item}</span>
                          </div>
                          {notExistStaffIDs.includes(item) && <div className={styles.validateMess}>Staff ID is not available</div>}
                        </div>
                      );
                    })}
                    <div className={styles.inputField}>
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onFocus={(e) => handleInputFocus(e)}
                        onPaste={handlePaste}
                        className={styles.input}
                        disabled={selectedItems.length >= MAX_STAFF_IDS}
                      />
                      {isLoading && <BasicLoading inline="true" pxsize={10} />}
                    </div>
                  </div>
                  <div className={styles.counter}>
                    {selectedItems.length}/{MAX_STAFF_IDS}
                  </div>
                </div>

                {showSuggestions.isShow && suggestions.length > 0 && (
                  <div className={styles.dropdownContainer} style={{ top: showSuggestions.top, left: showSuggestions.left }}>
                    <ul ref={dropdownRef} className={styles.dropdown}>
                      {suggestions.map((item, index) => (
                        <li
                          id={`suggestion-${index}`}
                          key={item}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectItem(item);
                          }}
                          className={`${styles.suggestionItem} ${index === activeSuggestionIndex ? styles.active : ''}`}
                        >
                          {item}
                        </li>
                      ))}
                      {isLoading && (
                        <li className={styles.loadingItem}>
                          <BasicLoading inline="true" pxsize={10} />
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </Spin>
          </div>
          <div className={styles.flexContent}>
            <div className={styles.label}>Select field(s)</div>

            <BasicTreeSelect
              onApply={true}
              treeDefaultExpandAll={true}
              dropdownStyle={{ boxShadow: 'none' }}
              options={TP_PRINT_SELECT_OPTIONS}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              radioOptions={radioOptions}
              setRadioOptions={setRadioOptions}
              onChangeRadio={handleChangeRadio}
              prevSelectedOption={prevSelectedOption}
              prevRadioOptions={prevRadioOptions}
              style={{ maxHeight: 350, width: '100%', position: 'relative', display: 'block' }}
            >
            </BasicTreeSelect>
          </div>
        </div>
      </div >
      <div className={styles.bottomBtn}>
        <BasicButton mode="outline" className={styles.viewSampleBtn} disabled={selectedItems.length == 0 || notExistStaffIDs.length || loadingSummary || loadingPersonality} onClick={handleViewSample}>
          View Sample {(loadingSummary || loadingPersonality) && <BasicLoading inline="true" pxsize={10} />}
        </BasicButton>
        <BasicButton mode="teal" disabled={selectedItems.length == 0 || notExistStaffIDs.length || downloading} onClick={handleDownload}>
          Download {downloading && <BasicLoading inline="true" pxsize={10} />}
        </BasicButton>
      </div>
      {
        isViewingSample && (
          <TalentProfileDownloadPreview
            carreerAspiration={carreerAspiration}
            personality={personality}
            langAndBoard={langAndBoard}
            qualification={qualification}
            lang={lang}
            areaOfExpertise={areaOfExpertise}
            roleUser={{ id: selectedItems[0], roleActive: user.roleActive, permissions: user.permissions }}
            isPrinting={isViewingSample}
            closeModal={closeModal}
            tokenTalentProfile={tokenTalentProfile}
            countOfDownloads={selectedItems.length}
            handleDownload={fetchDownload}
            downloading={downloading}
            getTalentProfileSummary={getTalentProfileSummary}
            loadingSummary={loadingSummary}
            loadingPersonality={loadingPersonality}
          />
        )
      }

      <div className={styles.tableWrapper}>
        <div className={styles.subTitle}>Download Activity Log</div>
        <Spin spinning={loading}>
          <DownloadActivityLogsTable
            downloadActivityLogsTable={downloadActivityLogsTable}
            setDownloadActivityLogsTable={setDownloadActivityLogsTable}
            fetchDownloadActivityLogsTable={fetchDownloadActivityLogsTable}
          />
        </Spin>
      </div>
    </>
  );
};

const DownloadActivityLogsTable = ({ downloadActivityLogsTable, setDownloadActivityLogsTable, fetchDownloadActivityLogsTable }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const columns = [
    {
      title: 'Requested Time',
      dataIndex: 'requested_time',
      width: '20%',
      render: (text) => <div className={styles.font_table}>{text}</div>
    },
    {
      title: 'Completed Time',
      dataIndex: 'completed_time',
      width: '20%',
      render: (text) => <div className={styles.font_table}>{text}</div>
    },
    {
      title: 'Action By',
      dataIndex: 'action_by',
      width: '20%',
      render: (text) => <div className={styles.font_table}>{text}</div>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '20%',
      render: (text) => <Status status={text} />
    },
    {
      title: 'File',
      dataIndex: 'file',
      width: '20%',
      render: (icon) => <div className={styles.fileIcon}>{icon}</div>
    },
  ];

  const handleDownloadFile = (url) => {
    if (!url) return;
    window.open(url, "_blank");
  };

  const handleAction = async (id, action) => {
    try {
      await adminApi.bulkDownloadTalentProfileAction(id, action, parseInt(user?.data?.userId), user.data.email);
      fetchDownloadActivityLogsTable();
    } catch (error) {
      console.error(`Failed ${action} for ${id}:`, error);
      dispatch(pushMessage({ message: AN_UNEXPECTED_ERROR, type: MESSAGE_TYPES.ERROR, timeShow: 3000 }));
    }
  };

  useEffect(() => {
    fetchDownloadActivityLogsTable();
  }, [downloadActivityLogsTable.page]);

  const paginationConfig = {
    total: downloadActivityLogsTable.total,
    current: downloadActivityLogsTable.page,
    showSizeChanger: false,
    defaultPageSize: downloadActivityLogsTable.limit,
    onChange: (pageNum) =>
      pageNum !== downloadActivityLogsTable.page && setDownloadActivityLogsTable((prev) => ({ ...prev, page: pageNum })),
    showTotal: (total, range) => (
      <>
        Showing <b>{range[0]}</b> to <b>{range[1]}</b> of <b>{total}</b> results
      </>
    ),
  };

  return (
    <div className={styles.table}>
      <BasicTable
        columns={columns}
        dataSource={downloadActivityLogsTable.data.map((item) => ({
          key: item.request_id,
          requested_time: moment(item.requested_at).format('DD/MM/YYYY HH:mm'),
          completed_time: item.completed_at ? moment(item.completed_at).format('DD/MM/YYYY HH:mm') : '-',
          action_by: item.user_name,
          status: item.isExpired ? 'EXPIRED' : item.status,
          file: !item.isExpired && item.user_email?.toLowerCase() === user.data.email?.toLowerCase() && (
            <div className={styles.actionIcons}>
              {item.zip_url_withToken && item.isDownloadable && (
                <img
                  src={download_file_icon}
                  alt="download"
                  className={`${styles.actionIcon} ${styles.download}`}
                  onClick={() => handleDownloadFile(item.zip_url_withToken)}
                />
              )}
              {item.status === 'IN_PROGRESS' && (
                <img
                  src={pause_icon}
                  alt="pause"
                  className={`${styles.actionIcon} ${styles.pause}`}
                  onClick={() => handleAction(item.request_id, 'PAUSE')}
                />
              )}
              {item.status === 'PAUSED' && (
                <img
                  src={play_icon}
                  alt="play"
                  className={`${styles.actionIcon} ${styles.play}`}
                  onClick={() => handleAction(item.request_id, 'PLAY')}
                />
              )}
              {['IN_PROGRESS', 'PAUSED', 'QUEUE'].includes(item.status) && (
                <img
                  src={close_icon}
                  alt="cancel"
                  className={`${styles.actionIcon} ${styles.cancel}`}
                  onClick={() => handleAction(item.request_id, 'CANCEL')}
                />
              )}
              {['FAILED', 'CANCELLED'].includes(item.status) && (
                <img
                  src={retry_icon}
                  alt="retry"
                  className={`${styles.actionIcon} ${styles.retry}`}
                  onClick={() => handleAction(item.request_id, 'RETRY')}
                />
              )}
            </div>
          )
        }))}
        pagination={{ position: ['bottomRight'], ...paginationConfig }}
      />
    </div>
  );
};

const Status = ({ status }) => {
  let statusClass = '';
  let displayText = status;

  switch (status) {
    case 'COMPLETED':
      statusClass = styles.completed;
      displayText = 'Completed';
      break;
    case 'IN_PROGRESS':
      statusClass = styles.inProgress;
      displayText = 'In Progress';
      break;
    case 'PAUSED':
      statusClass = styles.paused;
      displayText = 'Paused';
      break;
    case 'QUEUE':
      statusClass = styles.queue;
      displayText = 'Queue';
      break;
    case 'CANCELLED':
      statusClass = styles.cancelled;
      displayText = 'Cancelled';
      break;
    case 'FAILED':
      statusClass = styles.failed;
      displayText = 'Failed';
      break;
    case 'EXPIRED':
      statusClass = styles.expired;
      displayText = 'Expired';
      break;
    default:
      statusClass = '';
      displayText = status;
  }

  return <div className={`${styles.status} ${statusClass}`}>{displayText}</div>;
};


export default BulkDownloadTalentProfile;
